import { getStoreState } from '../store';
import { S } from './state';
import { prefersReducedMotion } from './motion';
import { nodeTint, ROOT_GRADIENT, toHex6 } from '../lib/color';
import {
  boxInside,
  FLIGHT_MS,
  flightAt,
  scaleOf,
  type Box,
  type FlightEnd,
  type PillMetrics,
} from '../lib/flight';
import type { NodeId } from '../lib/types';

/**
 * THE NODE IN FLIGHT (V0.47).
 *
 * A node lives on the map. When its popup opens it lifts off and lands in a seat at the top of
 * the popup; when a page opens from there it moves on to the bubble the document view draws;
 * every close is the trip back. This module is the one place that flies it — a clone in
 * `#flight`, above every surface it crosses between, sized and placed once per frame from
 * `lib/flight.ts`'s arithmetic, while the real boxes at both ends are told through the store's
 * `perch` whether the node is currently in them.
 *
 * Three decisions, all paid for already:
 *
 * 1. **The destination is re-measured every frame.** The document view's band converges over
 *    one to three layout passes after it appears (`useDocNavLayout`), and its titles land
 *    asynchronously; fixed keyframes aimed at frame 0 land where the bubble WAS. `to` is a
 *    function, and the last frame is wherever it answers then.
 * 2. **Every step has a timer deadline.** A hidden tab suspends `requestAnimationFrame`
 *    entirely; without the deadline the promise never settles, the perch stays `air`, and the
 *    node is simply gone from every surface. The prototype hit this. Timers are throttled in
 *    that state but they fire.
 * 3. **`prefers-reduced-motion` arrives.** No clone, no perch, `onEnd(true)` at once — so the
 *    regression suite, which runs that way, sees today's visibility on every surface.
 *
 * The animator's shape is the camera's (`mapController.ts`): a rAF loop, one write per frame,
 * exact landing, and one cancellation rule — a generation counter that every continuation
 * checks, so a click during a flight simply supersedes it.
 */

/** One end of a flight: the element, its box and dressing, and what to paint the clone with. */
export interface Anchor {
  el: HTMLElement;
  end: FlightEnd;
  label: string;
  /** `#rrggbb`, or null for the root, which is a gradient. */
  color: string | null;
}

export interface FlightSpec {
  id: NodeId;
  from: Anchor;
  /** RE-READ EVERY FRAME. Answering null mid-flight ends the flight where it stands. */
  to: () => Anchor | null;
  /** Where the node is once it lands; `null` = back on the map. */
  land: 'seat' | 'bubble' | null;
  /** Inline opacities eased alongside the flight, cleared after `onEnd`. */
  fades?: { el: HTMLElement; from: number; to: number }[];
  /** Called exactly once, on every path: landed (`true`) or cut short (`false`). */
  onEnd?: (completed: boolean) => void;
}

/* ------------------------------------------------------------------ *
 * Sequences and cancellation                                           *
 * ------------------------------------------------------------------ */

let gen = 0;

/**
 * Start a new sequence: whatever was in flight is over, and everything that was waiting on it
 * — camera callbacks, deadlines, element polls — will find its generation gone and step aside.
 * Returns the `alive()` those continuations check.
 */
export function beginSequence(): () => boolean {
  const alive = supersede();
  finish(false);
  return alive;
}

/**
 * The generation bump WITHOUT ending the clone in the air: for a close that begins while the
 * open is still landing, so the reverse can depart from where the clone is (`fly` reads
 * `current.last` for the same node) instead of from a seat it never reached.
 */
export function supersede(): () => boolean {
  gen += 1;
  const mine = gen;
  return () => gen === mine;
}

/** Stop the current flight where it stands. Its `onEnd` is called with `false`. */
export function cancelFlight(): void {
  finish(false);
}

/**
 * The one instant exit: everything in its place. The node sits in the popup's seat if the
 * popup is open on it, and on the map otherwise. Every path that decides NOT to fly ends here.
 */
export function restPerch(): void {
  // Under reduced motion nothing ever leaves the map, so nothing is ever perched: the seat
  // and the node are both simply drawn, exactly as they were before any of this existed.
  if (prefersReducedMotion()) {
    getStoreState().setPerch(null);
    return;
  }
  const id = S.popupFor;
  getStoreState().setPerch(id ? { id, at: 'seat' } : null);
}

/* ------------------------------------------------------------------ *
 * Anchors — measuring the three boxes a node can be in                 *
 * ------------------------------------------------------------------ */

const cssEscape = (v: string): string =>
  typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(v) : v;

const px = (v: string): number => parseFloat(v) || 0;

/** A box's dressing, from its computed style, scaled where the box is drawn scaled. */
function metricsOf(el: HTMLElement, scale: number): PillMetrics {
  const cs = getComputedStyle(el);
  const swatch = el.querySelector<HTMLElement>('.swatch');
  return {
    fontSize: px(cs.fontSize) * scale,
    padX: px(cs.paddingLeft) * scale,
    padY: px(cs.paddingTop) * scale,
    gap: px(cs.columnGap || cs.gap) * scale,
    swatch: (swatch ? swatch.offsetWidth : 0) * scale,
    radius: px(cs.borderTopLeftRadius) * scale,
  };
}

function anchorOf(el: HTMLElement | null, id: NodeId, scaled: boolean): Anchor | null {
  if (!el) return null;
  const r = el.getBoundingClientRect();
  if (!(r.width > 0) || !(r.height > 0)) return null;
  const n = getStoreState().nodes[id];
  if (!n) return null;
  const scale = scaled ? scaleOf(r.width, el.offsetWidth) : 1;
  return {
    el,
    end: {
      box: { left: r.left, top: r.top, width: r.width, height: r.height },
      metrics: metricsOf(el, scale),
    },
    label: n.label || '',
    color: n.root ? null : toHex6(n.color),
  };
}

const rectBox = (el: Element | null): Box | null => {
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return { left: r.left, top: r.top, width: r.width, height: r.height };
};

export const anchors = {
  /**
   * The node on the map — only while it is genuinely on the stage. A node panned past the
   * clipped edge has a rect but no presence, and a clone that lifted off from it would appear
   * from nowhere. Its metrics carry the camera scale: the rect is drawn at `k`, the style is not.
   */
  mapNode(id: NodeId): Anchor | null {
    const el = document.querySelector<HTMLElement>(`#world .node[data-id="${cssEscape(id)}"]`);
    const stage = rectBox(document.getElementById('stage'));
    const a = anchorOf(el, id, true);
    if (!a || !stage || !boxInside(a.end.box, stage)) return null;
    return a;
  },
  /** The seat at the top of an open popup. */
  seat(id: NodeId): Anchor | null {
    return anchorOf(
      document.querySelector<HTMLElement>(`#popup.show .aidoc-node.seat[data-id="${cssEscape(id)}"]`),
      id,
      false,
    );
  },
  /** The current node's bubble in the document view's band. */
  bubble(id: NodeId): Anchor | null {
    return anchorOf(
      document.querySelector<HTMLElement>(`#docnav-current > .docnav-node[data-id="${cssEscape(id)}"]`),
      id,
      false,
    );
  },
};

/* ------------------------------------------------------------------ *
 * The flight                                                           *
 * ------------------------------------------------------------------ */

interface Flight {
  id: NodeId;
  spec: FlightSpec;
  clone: HTMLElement;
  swatch: HTMLElement | null;
  start: number;
  frame: number;
  deadline: ReturnType<typeof setTimeout>;
  /** Where the clone is RIGHT NOW — the departure point of a reversal. */
  last: FlightEnd;
}

let current: Flight | null = null;

const now = (): number =>
  typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now();

function host(): HTMLElement | null {
  return document.getElementById('flight');
}

function makeClone(from: Anchor): { clone: HTMLElement; swatch: HTMLElement | null } {
  const clone = document.createElement('div');
  clone.className = 'flight-node' + (from.color ? '' : ' root');
  let swatch: HTMLElement | null = null;
  if (from.color) {
    const tint = nodeTint(from.color, getStoreState().isLight);
    clone.style.borderColor = tint.borderColor;
    clone.style.background = tint.background;
    clone.style.color = tint.color;
    swatch = document.createElement('span');
    swatch.className = 'swatch';
    swatch.style.background = from.color;
    clone.append(swatch);
  } else {
    clone.style.background = ROOT_GRADIENT;
  }
  const txt = document.createElement('span');
  txt.className = 'txt';
  txt.textContent = from.label;
  clone.append(txt);
  return { clone, swatch };
}

function paint(f: Flight, end: FlightEnd): void {
  const { box, metrics } = end;
  const st = f.clone.style;
  st.left = `${box.left}px`;
  st.top = `${box.top}px`;
  st.width = `${box.width}px`;
  st.height = `${box.height}px`;
  st.fontSize = `${metrics.fontSize}px`;
  st.padding = `${metrics.padY}px ${metrics.padX}px`;
  st.gap = `${metrics.gap}px`;
  st.borderRadius = `${metrics.radius}px`;
  if (f.swatch) {
    f.swatch.style.width = `${metrics.swatch}px`;
    f.swatch.style.height = `${metrics.swatch}px`;
  }
  f.last = end;
}

function paintFades(spec: FlightSpec, t: number): void {
  for (const fd of spec.fades ?? []) fd.el.style.opacity = String(fd.from + (fd.to - fd.from) * t);
}

/**
 * End the current flight, landed or not. Order matters and is fixed: the clone's last frame
 * is written, the perch moves so the destination box paints itself, the clone goes, `onEnd`
 * runs (which may commit a `display:none` through React), and ONLY THEN are the fades' inline
 * opacities cleared — so an element that has just been hidden never shows for one frame at
 * whatever opacity the fade left it.
 */
function finish(completed: boolean): void {
  const f = current;
  if (!f) return;
  current = null;
  if (f.frame) cancelAnimationFrame(f.frame);
  clearTimeout(f.deadline);
  if (completed) {
    const dest = f.spec.to();
    if (dest) paint(f, dest.end);
    paintFades(f.spec, 1);
  }
  const store = getStoreState();
  if (completed) store.setPerch(f.spec.land ? { id: f.id, at: f.spec.land } : null);
  else restPerch();
  f.clone.remove();
  f.spec.onEnd?.(completed);
  for (const fd of f.spec.fades ?? []) fd.el.style.opacity = '';
}

/**
 * Fly the node from `spec.from` to wherever `spec.to()` answers, over `FLIGHT_MS`.
 *
 * A flight already in the air for the same node is not cancelled and restarted: the new one
 * departs from the clone's current box, which is what makes an Escape mid-arrival a smooth
 * reversal rather than a jump back to the map and a second departure.
 */
export function fly(spec: FlightSpec): void {
  if (prefersReducedMotion() || typeof requestAnimationFrame === 'undefined' || !host()) {
    finish(false);
    // Arrive: both boxes are simply drawn, no clone between them and no perch — the map node
    // stays visible under the popup as it always has. The whole regression suite runs this way.
    getStoreState().setPerch(null);
    for (const fd of spec.fades ?? []) fd.el.style.opacity = '';
    spec.onEnd?.(true);
    return;
  }

  let from = spec.from.end;
  if (current && current.id === spec.id) from = current.last;
  finish(false);

  const { clone, swatch } = makeClone(spec.from);
  const f: Flight = {
    id: spec.id,
    spec,
    clone,
    swatch,
    start: now(),
    frame: 0,
    deadline: setTimeout(() => finish(true), FLIGHT_MS + 250),
    last: from,
  };
  current = f;
  getStoreState().setPerch({ id: spec.id, at: 'air' });
  paint(f, from);
  paintFades(spec, 0);
  host()!.append(clone);

  const step = (): void => {
    if (current !== f) return;
    const dest = spec.to();
    if (!dest) {
      finish(false);
      return;
    }
    const { box, metrics, t, done } = flightAt(from, dest.end, now() - f.start, FLIGHT_MS);
    paint(f, { box, metrics });
    paintFades(spec, t);
    if (done) finish(true);
    else f.frame = requestAnimationFrame(step);
  };
  f.frame = requestAnimationFrame(step);
}

/* ------------------------------------------------------------------ *
 * Waiting for React, and hiding what it is about to show               *
 * ------------------------------------------------------------------ */

/**
 * Poll for an element React has not rendered yet — the band's bubble, which appears a commit
 * or two after `openDoc()` returns. rAF-paced, bounded in frames AND in time, and abandoned
 * the moment the sequence it belongs to is over.
 */
export function awaitElement(
  find: () => HTMLElement | null,
  opts: { frames?: number; deadlineMs?: number; alive: () => boolean },
  cb: (el: HTMLElement | null) => void,
): void {
  const frames = opts.frames ?? 40;
  let tries = 0;
  let settled = false;
  const done = (el: HTMLElement | null): void => {
    if (settled) return;
    settled = true;
    clearTimeout(deadline);
    if (opts.alive()) cb(el);
  };
  const deadline = setTimeout(() => done(find()), opts.deadlineMs ?? 800);
  const tick = (): void => {
    if (settled) return;
    if (!opts.alive()) return done(null);
    const el = find();
    if (el && el.getBoundingClientRect().width > 0) return done(el);
    if (tries++ < frames) requestAnimationFrame(tick);
    else done(null);
  };
  tick();
}

/**
 * Set an inline opacity BEFORE React shows an element, so the first painted frame of the
 * document view is transparent and the flight's fade owns its arrival. Returns the clear; the
 * deadline clears regardless, because an overlay that stayed transparent would be a page that
 * never appeared.
 */
export function prefade(el: HTMLElement | null, opacity: number, deadlineMs = 900): () => void {
  if (!el) return () => {};
  el.style.opacity = String(opacity);
  const t = setTimeout(() => {
    el.style.opacity = '';
  }, deadlineMs);
  return () => {
    clearTimeout(t);
    el.style.opacity = '';
  };
}
